import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { apsisExhibitorsApi, fileUploadApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminExhibitors() {
  const [exhibitors, setExhibitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    description: '',
    logo: '',
    category: '',
    website: '',
    contact_email: '',
    contact_phone: ''
  });

  // Fetch exhibitors
  useEffect(() => {
    loadExhibitors();
  }, []);

  const loadExhibitors = async () => {
    try {
      setLoading(true);
      const data = await apsisExhibitorsApi.getAll();
      setExhibitors(data || []);
    } catch (err) {
      setError('Failed to load exhibitors: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await fileUploadApi.uploadImage(file, 'images', 'exhibitors');
      setFormData(prev => ({ ...prev, logo: url }));
      setLogoPreview(url);
    } catch (err) {
      setError('Failed to upload logo: ' + err.message);
    }
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await apsisExhibitorsApi.update(editingId, formData);
      } else {
        await apsisExhibitorsApi.create(formData);
      }
      resetForm();
      loadExhibitors();
    } catch (err) {
      setError('Failed to save exhibitor: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (exhibitor) => {
    setFormData(exhibitor);
    setLogoPreview(exhibitor.logo);
    setEditingId(exhibitor.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this exhibitor?')) {
      try {
        await apsisExhibitorsApi.delete(id);
        loadExhibitors();
      } catch (err) {
        setError('Failed to delete exhibitor: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      company_name: '',
      description: '',
      logo: '',
      category: '',
      website: '',
      contact_email: '',
      contact_phone: ''
    });
    setLogoPreview('');
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Loading exhibitors...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Manage APSIS Exhibitors</h2>
        <button 
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Add Exhibitor
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Exhibitor' : 'Add New Exhibitor'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group form-row full">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select category</option>
                    <option value="Cybersecurity">Cybersecurity</option>
                    <option value="Surveillance">Surveillance</option>
                    <option value="Access Control">Access Control</option>
                    <option value="AI & ML">AI & ML</option>
                    <option value="IoT">IoT</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group form-row full">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Company Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
                {logoPreview && (
                  <img src={logoPreview} alt="Logo Preview" className="image-preview" />
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Save Exhibitor
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exhibitors Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Category</th>
              <th>Contact Email</th>
              <th>Website</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {exhibitors.length === 0 ? (
              <tr><td colSpan="5" className="text-center">No exhibitors found</td></tr>
            ) : (
              exhibitors.map(exhibitor => (
                <tr key={exhibitor.id}>
                  <td>{exhibitor.company_name}</td>
                  <td><span style={{ backgroundColor: '#fef3c7', padding: '4px 12px', borderRadius: '4px', fontSize: '0.85rem' }}>{exhibitor.category}</span></td>
                  <td>{exhibitor.contact_email || 'N/A'}</td>
                  <td>{exhibitor.website ? <a href={exhibitor.website} target="_blank" rel="noopener noreferrer" style={{ color: '#0A66C2' }}>Visit</a> : 'N/A'}</td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(exhibitor)}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(exhibitor.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
